import * as React from 'react';
import Box from '@mui/joy/Box';
import Typography from '@mui/joy/Typography';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import BookingStatusChip from './BookingStatusChip';
import type { ChatProps } from "./Messages";

function formatShortDate(raw: string): string {
    try {
        const d = new Date(raw);
        return d.toLocaleString(undefined, {month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit'});
    } catch {
        return '';
    }
}

type ChatListItemProps = {
    id: string;
    unread?: boolean;
    sender: UserProps;
    messages: MessageProps[];
    selectedChatId?: string;
    setSelectedChat: (chat: ChatProps) => void;
    call_category?: string;
    has_sms?: boolean;
    has_whatsapp?: boolean;
    has_email?: boolean;
    unread_sms?: boolean;
    unread_whatsapp?: boolean;
    unread_email?: boolean;
    channel?: string;
    latest_booking_id?: number | null;
    latest_booking_status?: string | null;
    latest_booking_booked_in?: string | null;
    has_ronald_intervention?: boolean;
};

function ChatListItemInner(props: ChatListItemProps) {
    const {
        id, sender, messages, selectedChatId, setSelectedChat, call_category,
        has_sms, has_whatsapp, has_email, unread_sms, unread_whatsapp, unread_email,
        latest_booking_id, latest_booking_status, latest_booking_booked_in,
        has_ronald_intervention,
    } = props;
    const selected = selectedChatId === id;

    const latestMessage = React.useMemo(() => {
        const real = messages.filter((msg) => msg.leadData == null && msg.bookingData == null);
        if (real.length === 0) return null;
        return real.reduce((latest, current) =>
            new Date(current.timestamp).getTime() > new Date(latest.timestamp).getTime() ? current : latest
        );
    }, [messages]);

    const hasUnread = (unread_sms ?? false) || (unread_whatsapp ?? false) || (unread_email ?? false);
    const category = call_category || 'Sales';
    const hasBooking = !!latest_booking_id;
    const isRonaldMsg = latestMessage?.sender?.is_ai || latestMessage?.sender?.username?.endsWith('.ai');
    const isInbound = latestMessage?.sender?.isCustomer;

    const chatForSelection = React.useMemo<ChatProps>(() => ({
        id,
        sender,
        messages,
        call_category: category,
        has_sms,
        has_whatsapp,
        has_email,
        unread_sms,
        unread_whatsapp,
        unread_email,
        channel: props.channel,
        latest_booking_id,
        latest_booking_status,
        latest_booking_booked_in,
        has_ronald_intervention,
    }), [id, sender, messages, category, has_sms, has_whatsapp, has_email,
        unread_sms, unread_whatsapp, unread_email, props.channel,
        latest_booking_id, latest_booking_status, latest_booking_booked_in,
        has_ronald_intervention]);

    return (
        <Box
            onClick={() => setSelectedChat(chatForSelection)}
            sx={{
                p: 2,
                borderBottom: '1px solid',
                borderColor: '#f3f4f6',
                cursor: 'pointer',
                transition: 'background-color 0.15s',
                '&:hover': {bgcolor: '#f9fafb'},
                ...(selected
                    ? {bgcolor: '#eff6ff', borderLeft: '4px solid', borderLeftColor: '#3b82f6'}
                    : {}),
            }}
        >
            {/* Row 1: Name + chips + unread */}
            <Box sx={{display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap'}}>
                <Typography level="title-sm" noWrap sx={{fontWeight: 600}}>
                    {sender.name}
                </Typography>
                <Box
                    component="span"
                    sx={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        px: 0.75,
                        py: '2px',
                        fontSize: '10px',
                        fontWeight: 500,
                        lineHeight: 1.4,
                        borderRadius: '9999px',
                        flexShrink: 0,
                        ...(hasBooking
                            ? {bgcolor: '#dcfce7', color: '#15803d'}
                            : {bgcolor: '#fef3c7', color: '#b45309'}),
                    }}
                >
                    {hasBooking ? 'Booking' : 'Lead'}
                </Box>
                {hasBooking && (
                    <BookingStatusChip
                        status={latest_booking_status}
                        bookedIn={latest_booking_booked_in}
                        size="sm"
                    />
                )}
                {hasUnread && (
                    <Box sx={{width: 8, height: 8, borderRadius: '50%', bgcolor: '#3b82f6', flexShrink: 0}}/>
                )}
                {has_ronald_intervention && (
                    <WarningAmberRoundedIcon
                        sx={{fontSize: 14, color: '#f59e0b', flexShrink: 0}}
                        titleAccess="Ronald needs human help"
                    />
                )}
            </Box>

            {/* Row 2: Message preview */}
            {latestMessage && (
                <Typography
                    level="body-xs"
                    noWrap
                    sx={{
                        mt: 0.5,
                        color: '#6b7280',
                        display: '-webkit-box',
                        WebkitLineClamp: 1,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                    }}
                >
                    <Typography
                        component="span"
                        level="body-xs"
                        sx={{
                            fontWeight: isInbound ? 700 : 500,
                            color: isInbound
                                ? '#16a34a'
                                : isRonaldMsg
                                    ? '#7c3aed'
                                    : '#2563eb',
                        }}
                    >
                        {isInbound
                            ? 'Received: '
                            : isRonaldMsg
                                ? '🤖 Ronald: '
                                : `${latestMessage.sender?.username || 'Sent'}: `}
                    </Typography>
                    {latestMessage.content}
                </Typography>
            )}

            {/* Row 3: Timestamp */}
            {latestMessage && (
                <Typography level="body-xs" sx={{mt: 0.5, textAlign: 'right', color: '#9ca3af', fontSize: '11px'}}>
                    {formatShortDate(latestMessage.timestamp)}
                </Typography>
            )}
        </Box>
    );
}

const ChatListItem = React.memo(ChatListItemInner);
export default ChatListItem;
